import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, Gamepad2, ArrowRight, ArrowDown, ShieldCheck } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, openBillingPortal } from "@/lib/billing";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIOD_LABEL = { week: "week", month: "month", year: "year" };
const PATH = [
  { name: "Guest", sub: "Free" },
  { name: "Trading Trainer", sub: "Subscription" },
  { name: "MBM Beginner", sub: "4 Months" },
  { name: "MBM Advanced", sub: "4 Months" },
  { name: "MBM Professional", sub: "2 Months" },
  { name: "MBM Elite", sub: "Application" },
  { name: "MBM Capital", sub: "Future Vision" },
];

function fmt(n) { return `A$${Number(n).toLocaleString()}`; }

function PlanCard({ plan, onAction, loadingId, currentPlan }) {
  const [period, setPeriod] = useState("month");
  const isCurrent = currentPlan === plan.id;
  const featured = plan.featured;

  const priceBlock = () => {
    if (plan.type === "guest") return <span className="font-heading text-4xl font-light silver-text">FREE</span>;
    if (plan.type === "application") return <span className="font-heading text-2xl font-light silver-text">Application Only</span>;
    if (plan.type === "subscription") {
      const periods = Object.keys(plan.prices || {});
      return (
        <>
          <div className="flex items-center gap-1 rounded-full border border-white/12 p-1 mb-4 w-fit" data-testid={`period-toggle-${plan.id}`}>
            {periods.map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`text-[10px] tracking-wide rounded-full px-3 py-1.5 transition-colors ${period === p ? "bg-[#FAFAFA] text-[#0A0A0A]" : "text-zinc-400 hover:text-white"}`}
                data-testid={`period-${plan.id}-${p}`}>
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-1">
            <span className="font-heading text-4xl font-light silver-text">{fmt(plan.prices[period])}</span>
            <span className="text-sm text-zinc-500 font-light mb-1.5">/ {PERIOD_LABEL[period]}</span>
          </div>
          {period === "year" && plan.year_saving ? <div className="mt-1 text-[11px] text-[#7ee0a1]">Save {fmt(plan.year_saving)}</div> : null}
        </>
      );
    }
    // program
    return (
      <>
        <div className="flex items-end gap-1">
          <span className="font-heading text-4xl font-light silver-text">{fmt(plan.price)}</span>
          <span className="text-sm text-zinc-500 font-light mb-1.5">/ month</span>
        </div>
        <div className="mt-2 text-[12px] text-zinc-400">Guided {plan.duration_months} month program · renews monthly until cancelled</div>
      </>
    );
  };

  const handle = () => {
    if (plan.type === "guest") return onAction({ kind: "guest" });
    if (plan.type === "application") return onAction({ kind: "apply" });
    if (plan.type === "subscription") return onAction({ kind: "checkout", id: plan.id, period });
    return onAction({ kind: "checkout", id: plan.id, period: "month" }); // program
  };

  return (
    <div className={`glass-card h-full flex flex-col p-7 ${featured ? "glass-card-featured" : ""}`} data-testid={`pricing-card-${plan.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="section-label text-[10px] text-[#9fd3ff] flex items-center gap-1.5">
          {plan.id === "trading_trainer" && <Gamepad2 className="h-3.5 w-3.5" />}
          {plan.name}
        </span>
        {plan.badge && (
          <span className="section-label text-[8px] px-2 py-1 rounded-full border" style={{ color: plan.badge_color, borderColor: `${plan.badge_color}55` }} data-testid={`badge-${plan.id}`}>
            {plan.badge}
          </span>
        )}
        {featured && !plan.badge && <span className="section-label text-[8px] text-[#0A0A0A] bg-[#D3D3D3] px-2 py-1">Ongoing</span>}
      </div>

      {plan.tagline && <div className="mt-2 text-[12px] text-zinc-500 font-light">{plan.tagline}</div>}
      <div className="mt-5">{priceBlock()}</div>

      {plan.type === "program" && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-zinc-400" data-testid={`program-note-${plan.id}`}>
          <ShieldCheck className="h-3.5 w-3.5 text-[#9fd3ff]" /> Coaching program · billed monthly · cancel anytime
        </div>
      )}

      <ul className="mt-6 space-y-2.5 flex-1">
        {(plan.includes || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2.5" data-testid={`pricing-feature-${plan.id}-${i}`}>
            <Check strokeWidth={1.5} className="h-4 w-4 text-[#7ee0a1] mt-0.5 shrink-0" />
            <span className="text-[13px] text-zinc-300 font-light">{f}</span>
          </li>
        ))}
      </ul>

      <button onClick={handle} disabled={loadingId === plan.id || isCurrent}
        className={`mt-7 w-full py-3 text-sm font-medium tracking-wide transition-colors duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-60 ${featured ? "btn-solid" : "btn-glass text-white"}`}
        data-testid={`pricing-cta-${plan.id}`}>
        {loadingId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Current plan" : (
          <>{plan.type === "subscription" && <Sparkles className="h-4 w-4" />}{plan.cta}</>
        )}
      </button>
    </div>
  );
}

function AddonCard({ addon, onBuy, loadingId }) {
  const [yearly, setYearly] = useState(false);
  const price = yearly ? addon.prices.year : addon.prices.month;
  const save = addon.prices.month * 12 - addon.prices.year;
  return (
    <div className="glass-card p-6 flex flex-col" data-testid={`addon-card-${addon.id}`}>
      <div className="flex items-center justify-between">
        <span className="section-label text-[10px] text-[#9fd3ff]">{addon.name}</span>
        <div className="flex items-center gap-1 rounded-full border border-white/12 p-0.5">
          {["month", "year"].map((p) => (
            <button key={p} onClick={() => setYearly(p === "year")}
              className={`text-[9px] rounded-full px-2 py-1 transition-colors ${(yearly ? "year" : "month") === p ? "bg-[#FAFAFA] text-[#0A0A0A]" : "text-zinc-400"}`}
              data-testid={`addon-${addon.id}-${p}`}>
              {p === "month" ? "Monthly" : "Yearly"}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[12px] text-zinc-500 font-light min-h-[32px]">{addon.tagline}</p>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-heading text-3xl font-light silver-text">{fmt(price)}</span>
        <span className="text-xs text-zinc-500 mb-1.5">/ {yearly ? "yr" : "mo"}</span>
      </div>
      {yearly && save > 0 && <div className="text-[11px] text-[#7ee0a1] mt-1">Save {fmt(save)}</div>}
      <button onClick={() => onBuy(addon.id, yearly ? "year" : "month")} disabled={loadingId === `addon-${addon.id}`}
        className="btn-glass text-white mt-5 w-full py-2.5 text-sm font-medium disabled:opacity-60" data-testid={`addon-cta-${addon.id}`}>
        {loadingId === `addon-${addon.id}` ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Add subscription"}
      </button>
    </div>
  );
}

export default function Pricing() {
  const { user, entitlements } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    fetch(`${API}/billing/plans`).then((r) => r.json()).then(setData).catch(() => {});
  }, []);

  const doCheckout = async (kind, itemId, period) => {
    if (!user) { navigate("/login"); return; }
    setLoadingId(kind === "addon" ? `addon-${itemId}` : itemId);
    try {
      const ok = await startCheckout({ kind, itemId, period });
      if (!ok) navigate("/login");
    } catch (e) { toast.error(e.message); } finally { setLoadingId(null); }
  };

  const onPlanAction = (a) => {
    if (a.kind === "guest") { user ? navigate("/learn") : navigate("/login"); return; }
    if (a.kind === "apply") { window.location.href = "/#apply"; return; }
    doCheckout("plan", a.id, a.period);
  };

  if (!data) return <section id="pricing" className="py-24" />;

  return (
    <section id="pricing" className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><SectionLabel>Memberships</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-3xl">
            A structured path from first candle to funded trader.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-sm text-zinc-500 font-light max-w-2xl">
            MBM is a premium education platform, not just another subscription. Trading Trainer is your ongoing
            software subscription; the Academy programs are monthly coaching memberships with a guided program length.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#9fd3ff]/30 bg-[#9fd3ff]/[0.06] px-4 py-2" data-testid="pricing-intake-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7ee0a1]" style={{ boxShadow: "0 0 8px #7ee0a1" }} />
              <span className="section-label text-[10px] text-[#9fd3ff]">Academy programs start August 2026 · Trading Trainer available immediately</span>
            </div>
            {user && entitlements && !entitlements.is_staff && entitlements.membership !== "guest" && (
              <button
                onClick={async () => { try { await openBillingPortal(); } catch (e) { toast.error(e.message); } }}
                className="btn-glass text-white px-4 py-2 text-xs font-medium tracking-wide"
                data-testid="manage-billing-btn">
                Manage Billing
              </button>
            )}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
          {data.plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <PlanCard plan={p} onAction={onPlanAction} loadingId={loadingId} currentPlan={entitlements?.membership} />
            </Reveal>
          ))}
        </div>

        {/* Add-ons */}
        <div className="mt-24" data-testid="addons-section">
          <Reveal><SectionLabel>MBM Add-Ons</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-sm text-zinc-500 font-light max-w-2xl">
              Optional subscriptions you can add to any membership. Billed monthly or yearly.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.addons.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <AddonCard addon={a} onBuy={(id, per) => doCheckout("addon", id, per)} loadingId={loadingId} />
              </Reveal>
            ))}
          </div>
        </div>

        {/* Learning path */}
        <div className="mt-24" data-testid="learning-path">
          <Reveal><SectionLabel>The Learning Path</SectionLabel></Reveal>
          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch gap-3">
            {PATH.map((step, i) => (
              <Reveal key={step.name} delay={i * 0.05}>
                <div className="flex items-center gap-3">
                  <div className={`glass-card px-5 py-4 min-w-[150px] ${step.name === "MBM Capital" ? "opacity-60" : ""}`}>
                    <div className="text-sm text-zinc-100 font-medium">{step.name}</div>
                    <div className="section-label text-[8px] text-zinc-500 mt-1">{step.sub}</div>
                  </div>
                  {i < PATH.length - 1 && (
                    <>
                      <ArrowRight className="h-4 w-4 text-zinc-600 hidden sm:block shrink-0" />
                      <ArrowDown className="h-4 w-4 text-zinc-600 sm:hidden" />
                    </>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-16 text-xs text-zinc-600 font-light max-w-3xl">
            Prices in AUD. All paid memberships and add-ons renew automatically each billing period until cancelled.
            You can cancel anytime from Manage Billing in your account; access continues to the end of the paid period.
            All content is educational and for analysis only; trading involves risk and no profit is guaranteed.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
